'use client';
import React, { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import Player from '@vimeo/player';
import classNames from 'classnames';

export interface IVimeoPlayerHandle {
  openFullscreen(): void;
  play(): void;
  pause(): void;
}

export interface IVimeoPlayerProps {
  onAutoPlayStarted?: () => void;
  onPayerReady?: () => void;
  media: any;
  cover?: boolean;
  controls?: boolean;
}

const VimeoPlayer = forwardRef(function VimeoPlayer(
  { media, cover, controls, onPayerReady, onAutoPlayStarted, ...props }: IVimeoPlayerProps,
  ref,
) {
  const root = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);

  const player = useRef<Player>(null);
  const fullscreen = useRef<boolean>(false);
  const [show, setShow] = useState(!cover);
  const [vimeoReady, setVimeoReady] = useState(false);

  const [videobgWidth, setVideobgWidth] = useState('100%');
  const [videobgHeight, setVideobgHeight] = useState('100%');

  useEffect(() => {
    if (!isNaN(media.vimeoId) && container.current) {
      player.current = new Player(container.current, {
        id: media.vimeoId,
        autoplay: media.autoPlay,
        background: media.autoPlay,
        loop: media.loop,
        controls: controls,
        dnt: true,
      });

      if (media.autoPlay && onAutoPlayStarted) {
        player.current.play().then(() => {
          onAutoPlayStarted();
        });
      }

      player.current.ready().then(() => {
        setVimeoReady(true);
        if (onPayerReady) {
          onPayerReady();
        }
      });
    } else {
      console.error(`'${media.vimeoId}' is not a valid vimeo ID`);
    }
  }, [onPayerReady, onAutoPlayStarted, media, controls]);

  useImperativeHandle(ref, () => {
    const monitorFullScreen = () => {
      if (document.fullscreenElement !== null) {
        setTimeout(() => {
          monitorFullScreen();
        }, 100);
      } else {
        if (fullscreen.current) {
          fullscreen.current = false;
        }
      }
    };

    return {
      openFullscreen() {
        if (
          (media.allowFullScreen === undefined || media.allowFullScreen === true) &&
          !fullscreen.current &&
          player.current
        ) {
          fullscreen.current = true;
          player.current.requestFullscreen();
          player.current.play();
          setTimeout(() => {
            monitorFullScreen();
          }, 1000);
        }
      },
      play() {
        player.current?.play();
      },
      pause() {
        player.current?.pause();
      },
    };
  }, [media]);

  useEffect(() => {
    if (vimeoReady && cover) {
      videobgEnlarge();
      window.addEventListener('resize', videobgEnlarge);
      return () => window.removeEventListener('resize', videobgEnlarge);
    }
  }, [vimeoReady, cover]);

  const videobgEnlarge = async () => {
    const w = await player.current?.getVideoWidth();
    const h = await player.current?.getVideoHeight();
    const videoAspect = (h || 1) / (w || 1);
    const parentAspect =
      (root.current?.parentElement?.offsetHeight || 1) / (root.current?.parentElement?.offsetWidth || 1);

    if (parentAspect > videoAspect) {
      setVideobgWidth((parentAspect / videoAspect) * 100 + '%');
      setVideobgHeight('100%');
    } else {
      setVideobgWidth('100%');
      setVideobgHeight((videoAspect / parentAspect) * 100 + '%');
    }
    setShow(true);
  };

  if (!media) {
    return null;
  }

  return (
    <>
      <div
        ref={root}
        className={classNames(
          'absolute left-1/2 top-1/2 h-full w-full -translate-x-2/4 -translate-y-2/4',
          !show && 'hidden',
        )}
        style={{ width: videobgWidth, height: videobgHeight }}
      >
        <div className="vimeo h-full w-full [&_iframe]:h-full [&_iframe]:w-full" ref={container} {...props} />
      </div>
    </>
  );
});

export default VimeoPlayer;
